/**
 * Tooltip Overlay Component for WebGLChart
 * Shows timestamp and value when hovering over the chart
 */

import React from 'react';
import { CHART_PADDING, LOCALE } from '../constants';

interface TooltipData {
  x: number;        // Screen X position
  y: number;        // Screen Y position
  timestamp: number; // Unix timestamp in seconds
  value: number;    // Data value
}

interface TooltipOverlayProps {
  data: TooltipData | null;
  chartHeight: number;
}

export const TooltipOverlay: React.FC<TooltipOverlayProps> = ({
  data,
  chartHeight,
}) => {
  if (!data) return null;

  // Format timestamp for display
  const formatTimestamp = (ts: number): string => {
    if (!Number.isFinite(ts) || ts <= 0) return '--';

    const d = new Date(ts * 1000);
    if (isNaN(d.getTime())) return '--';

    return new Intl.DateTimeFormat(LOCALE, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(d);
  };

  // Format value for display
  const formatValue = (val: number): string => {
    if (!Number.isFinite(val)) return '--';

    if (Math.abs(val) < 0.01) {
      return val.toExponential(3);
    } else if (Math.abs(val) < 1) {
      return val.toLocaleString(LOCALE, { maximumFractionDigits: 4 });
    } else if (Math.abs(val) < 1000) {
      return val.toLocaleString(LOCALE, { maximumFractionDigits: 2 });
    } else {
      return val.toLocaleString(LOCALE, { maximumFractionDigits: 1 });
    }
  };

  // Position tooltip - flip if too close to edges
  const tooltipWidth = 180;
  const tooltipHeight = 50;
  const flipX = data.x > tooltipWidth + 20;
  const flipY = data.y < tooltipHeight + 20;

  return (
    <>
      {/* Crosshair lines */}
      <div
        className="absolute pointer-events-none bg-gray-400"
        style={{
          left: CHART_PADDING.left + data.x,
          top: CHART_PADDING.top,
          width: 1,
          height: chartHeight,
          opacity: 0.5,
        }}
      />
      <div
        className="absolute pointer-events-none bg-gray-400"
        style={{
          left: CHART_PADDING.left,
          top: CHART_PADDING.top + data.y,
          width: '100%',
          height: 1,
          opacity: 0.5,
        }}
      />

      {/* Data point marker */}
      <div
        className="absolute pointer-events-none rounded-full bg-primary border-2 border-white shadow-md"
        style={{
          left: CHART_PADDING.left + data.x - 5,
          top: CHART_PADDING.top + data.y - 5,
          width: 10,
          height: 10,
        }}
      />

      {/* Tooltip box */}
      <div
        className="absolute pointer-events-none bg-gray-900 text-white text-xs rounded-md shadow-lg px-2.5 py-1.5 z-20"
        style={{
          left: CHART_PADDING.left + data.x + (flipX ? -tooltipWidth - 10 : 10),
          top: CHART_PADDING.top + data.y + (flipY ? 10 : -tooltipHeight - 10),
          minWidth: tooltipWidth,
        }}
      >
        <div className="font-medium text-gray-300 mb-0.5">
          {formatTimestamp(data.timestamp)}
        </div>
        <div className="font-bold text-accent">
          {formatValue(data.value)}
        </div>
      </div>
    </>
  );
};

export type { TooltipData };
